import React, { useState, useRef, useEffect } from 'react';
import { Upload, Stamp, Trash2, Save, Image as ImageIcon, Palette, Type, Circle, Square } from 'lucide-react';
import { toast } from 'sonner';

export const CompanyStamp = ({ 
  onSave, 
  initialStamp = null,
  title = "Firmenstempel",
  subtitle = "Erstellen Sie einen digitalen Stempel oder laden Sie Ihren eigenen hoch"
}) => {
  const [activeTab, setActiveTab] = useState('generator'); // 'generator' or 'upload'
  const [stampImage, setStampImage] = useState(initialStamp);
  const [previewUrl, setPreviewUrl] = useState(initialStamp);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Generator state
  const [stampConfig, setStampConfig] = useState({
    companyName: '',
    slogan: '',
    managingDirector: '',
    address: '',
    postalCode: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    taxNumber: '',
    commercialRegister: '',
    shape: 'circle', // 'circle' or 'rectangle'
    color: '#1e40af', // Blue
    fontSize: 16,
    style: 'classic' // 'classic', 'modern', 'minimal'
  });

  // Generate stamp on canvas
  useEffect(() => {
    if (activeTab === 'generator' && canvasRef.current) {
      generateStamp();
    }
  }, [stampConfig, activeTab]);

  const generateStamp = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Set style
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    const centerX = width / 2;
    const centerY = height / 2;

    // Draw shape
    ctx.strokeStyle = stampConfig.color;
    ctx.lineWidth = 4;
    ctx.font = `bold ${stampConfig.fontSize}px Arial`;
    ctx.fillStyle = stampConfig.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (stampConfig.shape === 'circle') {
      // Draw circle
      const radius = 140;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.stroke();

      // Draw company name on top arc
      if (stampConfig.companyName) {
        const text = stampConfig.companyName.toUpperCase();
        drawTextAlongArc(ctx, text, centerX, centerY, radius - 20, -Math.PI, -0.1);
      }

      // Draw address/phone on bottom arc
      if (stampConfig.address || stampConfig.phone) {
        const bottomText = stampConfig.address || stampConfig.phone;
        ctx.font = `${stampConfig.fontSize - 4}px Arial`;
        drawTextAlongArc(ctx, bottomText, centerX, centerY, radius - 20, 0.1, Math.PI);
      }

      // Draw website in center
      if (stampConfig.website) {
        ctx.font = `bold ${stampConfig.fontSize}px Arial`;
        ctx.fillText(stampConfig.website, centerX, centerY);
      }

      // Draw inner circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius - 10, 0, 2 * Math.PI);
      ctx.stroke();

    } else {
      // Draw rectangle
      const rectWidth = 280;
      const rectHeight = 120;
      const x = centerX - rectWidth / 2;
      const y = centerY - rectHeight / 2;
      
      ctx.strokeRect(x, y, rectWidth, rectHeight);
      ctx.strokeRect(x + 5, y + 5, rectWidth - 10, rectHeight - 10);

      // Draw text in rectangle
      let textY = centerY - 30;
      
      if (stampConfig.companyName) {
        ctx.font = `bold ${stampConfig.fontSize + 2}px Arial`;
        ctx.fillText(stampConfig.companyName.toUpperCase(), centerX, textY);
        textY += 25;
      }

      if (stampConfig.address) {
        ctx.font = `${stampConfig.fontSize - 2}px Arial`;
        ctx.fillText(stampConfig.address, centerX, textY);
        textY += 20;
      }

      if (stampConfig.phone) {
        ctx.font = `${stampConfig.fontSize - 2}px Arial`;
        ctx.fillText(stampConfig.phone, centerX, textY);
        textY += 20;
      }

      if (stampConfig.website) {
        ctx.font = `${stampConfig.fontSize - 2}px Arial`;
        ctx.fillText(stampConfig.website, centerX, textY);
      }
    }
  };

  const drawTextAlongArc = (ctx, text, centerX, centerY, radius, startAngle, endAngle) => {
    const angleRange = endAngle - startAngle;
    const charAngle = angleRange / text.length;
    
    for (let i = 0; i < text.length; i++) {
      const angle = startAngle + (i + 0.5) * charAngle;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle + Math.PI / 2);
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
  };

  const saveGeneratedStamp = () => {
    if (!canvasRef.current) return;
    
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setStampImage(dataUrl);
    setPreviewUrl(dataUrl);
    onSave(dataUrl);
    toast.success('✅ Stempel generiert und gespeichert!');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei aus');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Die Datei ist zu groß (max. 5MB)');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setPreviewUrl(dataUrl);
      setStampImage(dataUrl);
      toast.success('Stempel-Vorschau geladen');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!stampImage) {
      toast.error('Bitte laden Sie zuerst einen Stempel hoch');
      return;
    }
    onSave(stampImage);
  };

  const handleClear = () => {
    setStampImage(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.info('Stempel entfernt');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
          <Stamp className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{subtitle}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('generator')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'generator'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="generator-tab"
        >
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Generator
          </div>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'upload'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="upload-tab"
        >
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload
          </div>
        </button>
      </div>

      <div className="space-y-4">
        {/* Generator Tab */}
        {activeTab === 'generator' && (
          <>
            {/* Configuration Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Firmenname *</label>
                <input
                  type="text"
                  value={stampConfig.companyName}
                  onChange={(e) => setStampConfig({...stampConfig, companyName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="z.B. Easyclaim24 GmbH"
                  data-testid="stamp-company-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Slogan</label>
                <input
                  type="text"
                  value={stampConfig.slogan}
                  onChange={(e) => setStampConfig({...stampConfig, slogan: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="z.B. Ihr Partner für Glasreparatur"
                  data-testid="stamp-slogan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Geschäftsführer</label>
                <input
                  type="text"
                  value={stampConfig.managingDirector}
                  onChange={(e) => setStampConfig({...stampConfig, managingDirector: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Max Mustermann"
                  data-testid="stamp-managing-director"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                <input
                  type="text"
                  value={stampConfig.address}
                  onChange={(e) => setStampConfig({...stampConfig, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Musterstraße 123"
                  data-testid="stamp-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PLZ</label>
                  <input
                    type="text"
                    value={stampConfig.postalCode}
                    onChange={(e) => setStampConfig({...stampConfig, postalCode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="12345"
                    data-testid="stamp-postal-code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stadt</label>
                  <input
                    type="text"
                    value={stampConfig.city}
                    onChange={(e) => setStampConfig({...stampConfig, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Berlin"
                    data-testid="stamp-city"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="text"
                    value={stampConfig.phone}
                    onChange={(e) => setStampConfig({...stampConfig, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="+49 30 12345678"
                    data-testid="stamp-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
                  <input
                    type="email"
                    value={stampConfig.email}
                    onChange={(e) => setStampConfig({...stampConfig, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="info@firma.de"
                    data-testid="stamp-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="text"
                  value={stampConfig.website}
                  onChange={(e) => setStampConfig({...stampConfig, website: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="www.firma.de"
                  data-testid="stamp-website"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Steuernummer</label>
                  <input
                    type="text"
                    value={stampConfig.taxNumber}
                    onChange={(e) => setStampConfig({...stampConfig, taxNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="DE123456789"
                    data-testid="stamp-tax-number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Handelsregister</label>
                  <input
                    type="text"
                    value={stampConfig.commercialRegister}
                    onChange={(e) => setStampConfig({...stampConfig, commercialRegister: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="HRB 12345"
                    data-testid="stamp-commercial-register"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Form</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setStampConfig({...stampConfig, shape: 'circle'})}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        stampConfig.shape === 'circle'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      data-testid="shape-circle"
                    >
                      <Circle className="w-5 h-5 mx-auto" />
                      <span className="text-xs mt-1 block">Kreis</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setStampConfig({...stampConfig, shape: 'rectangle'})}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        stampConfig.shape === 'rectangle'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      data-testid="shape-rectangle"
                    >
                      <Square className="w-5 h-5 mx-auto" />
                      <span className="text-xs mt-1 block">Rechteck</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Farbe</label>
                  <div className="flex gap-2">
                    {['#1e40af', '#dc2626', '#059669', '#7c3aed', '#000000'].map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setStampConfig({...stampConfig, color})}
                        className={`w-10 h-10 rounded-lg border-2 transition-all ${
                          stampConfig.color === color ? 'border-gray-900 scale-110' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Canvas */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-700 mb-3 text-center">Vorschau</p>
              <div className="flex justify-center">
                <canvas
                  ref={canvasRef}
                  width={320}
                  height={320}
                  className="border border-gray-300 rounded bg-white"
                />
              </div>
            </div>

            {/* Generator Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setStampConfig({
                  companyName: '',
                  address: '',
                  phone: '',
                  website: '',
                  shape: 'circle',
                  color: '#1e40af',
                  fontSize: 16,
                  style: 'classic'
                })}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="reset-generator-button"
              >
                <Trash2 className="w-4 h-4" />
                Zurücksetzen
              </button>
              <button
                type="button"
                onClick={saveGeneratedStamp}
                disabled={!stampConfig.companyName}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-stamp-button"
              >
                <Save className="w-4 h-4" />
                Stempel generieren
              </button>
            </div>
          </>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <>
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all"
              onClick={() => fileInputRef.current?.click()}
            >
              {previewUrl && activeTab === 'upload' ? (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <img 
                      src={previewUrl} 
                      alt="Firmenstempel Vorschau" 
                      className="max-h-40 rounded-lg border border-gray-300 bg-white p-2"
                    />
                  </div>
                  <p className="text-sm text-gray-600">Klicken Sie, um einen anderen Stempel hochzuladen</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-base font-medium text-gray-900 mb-1">
                    Firmenstempel hochladen
                  </p>
                  <p className="text-sm text-gray-500">
                    PNG, JPG oder SVG - Max. 5MB
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="stamp-file-input"
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ImageIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-1">Empfehlungen für Ihren Firmenstempel</p>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Transparenter Hintergrund (PNG) für beste Ergebnisse</li>
                    <li>• Auflösung: Mindestens 300 x 300 Pixel</li>
                    <li>• Kreisförmige oder rechteckige Form</li>
                    <li>• Klare, gut lesbare Schrift</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Upload Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClear}
                disabled={!stampImage}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="clear-stamp-button"
              >
                <Trash2 className="w-4 h-4" />
                Entfernen
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!stampImage}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-stamp-button"
              >
                <Save className="w-4 h-4" />
                Speichern
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
